import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { map } from 'rxjs/operators';
import { GlobalFunctions } from '@global/GlobalFunctions';

/**
 * Account Featuers
 */
export enum AccountFeatures {
	PRISMPdM = 1,
	SAP = 2,
	Data_Verification = 3,
	Automatic_Lubrication = 4,
	Watch_List = 5,
	Actions = 6,
	Compare_Assets = 7,
	Tile_Customisation = 8,
	Sparkle_Line = 9,
	Energy_Monitoring = 10,
	Asset_Information = 11,
	Virtual_Sensors = 12,
	Average_Trend_Line = 13
}

/**
 * IFeature
 */
export interface IFeature {
	/** FeatureID */
	FeatureID: number;
	/** FeatureName */
	FeatureName: string;
	/** FeatureDescription */
	FeatureDescription: string;
}

/**
 * Feature
 */
export class Feature implements IFeature {
	/**
	 * Default Constructor
	 * @param FeatureID FeatureID 
	 * @param FeatureName FeatureName 
	 * @param FeatureDescription FeatureDescription 
	 */
	constructor(
		public FeatureID: number,
		public FeatureName: string,
		public FeatureDescription: string
	) { }
}

/**
 * AccountRole
 * @enum {number}
 * @description
 * Enum for Account Roles with increasing levels of access.
 */
export enum AccountRole {
	Demo = 0,
	User = 10,
	Engineer = 20,
	AccountOwner = 30,
	AccountBilling = 40,
	AccountManager = 50,
	AccountAdministrator = 60,
	SystemAdministrator = 999
}

/**
 * Account Role Labels
 * @type {Record<AccountRole, string>}
 * @description
 * A mapping of AccountRole enum values to their string labels for display purposes.
 * This is useful for UI components to show human-readable names for each role.
 * @example
 * ```typescript
 * const roleLabel = AccountRoleLabels[AccountRole.Engineer]; // "Engineer"
 * ```
 */
export const AccountRoleLabels: Record<AccountRole, string> = {
	[AccountRole.Demo]: "Demo",
	[AccountRole.User]: "User",
	[AccountRole.Engineer]: "Engineer",
	[AccountRole.AccountOwner]: "Account Owner",
	[AccountRole.AccountBilling]: "Account Billing",
	[AccountRole.AccountManager]: "Account Manager",
	[AccountRole.AccountAdministrator]: "Account Administrator",
	[AccountRole.SystemAdministrator]: "System Administrator"
};

/**
 * resolve Account Role
 * @param value
 * @description
 * This function takes a numeric value and returns the highest AccountRole that is less than or equal to the value.
 * It effectively finds the "floor" of the AccountRole levels based on the provided value.
 * @returns
 * {AccountRole}
 * @example
 * ```typescript
 * const role = resolveAccountRole(25); // Returns AccountRole.Engineer (20)
 * ```
 */
export function resolveAccountRole(value: number): AccountRole {
	const levels = Object.values(AccountRole)
		.filter(v => typeof v === 'number') as number[];

	// Filter out levels that are greater than the input value
	const lowerLevels = levels.filter(level => level <= value);

	// If no lower level exists (value is below all defined levels), fallback to the lowest
	if (lowerLevels.length === 0) {
		return Math.min(...levels) as AccountRole;
	}

	// Return the maximum of the lower/equal levels (i.e. the floor)
	const nearest = Math.max(...lowerLevels);
	return nearest as AccountRole;
}

/**
 * Interface for User Preferences
 */
export interface IUserPreferences {
	/** Default Trend Duration */
	TrendDuration: string;
}

/**
 * User Preferences
 */
export class UserPreferences implements IUserPreferences {
	TrendDuration: string;
}

/**
 * Interface for Database Account Object
 * @help
 * let account = this.accounts.find(({ id }) => id === "<ID String>")
 */
export interface IAccount {
	/** Account ID */
	ID: number;
	/** Account UID */
	GUID: string
	/** Account Name */
	Name: string;
	/** Account Description */
	Description: string;
	/** Account owner */
	Owner: string;
	/** Account owner ID */
	OwnerID: number;
	/** Account Marked as disabled*/
	Disabled: boolean;
	/*** Default Account */
	Default: boolean;
	/** Account Address */
	Address: string;
	/** User is an account Admin */
	Admin: boolean;
	/** RemindersEnabled */
	RemindersEnabled: boolean;
	/** ReminderInterval */
	ReminderInterval: number;
	/** Modified */
	Modified: number;
	/** Features */
	Features: any;
	/** Account is a Trial Account */
	Trial: boolean;
	/** Account Expiry Date */
	Expiry: number;
	/** Account Closed */
	Closed: boolean
}

/**
 * Interface for Database Account Object
 */
export class Account implements IAccount {
	/**
	 * Account
	 * @param ID ID
	 * @param GUID GUID
	 * @param Name Name
	 * @param Description Description
	 * @param Owner Owner
	 * @param OwnerID OwnerID
	 * @param Disabled Disabled
	 * @param appEnergy appEnergy
	 * @param appVibration appVibration
	 * @param Default Default
	 * @param Address Address
	 * @param Admin Admin
	 * @param Modified Modified
	 * @param RemindersEnabled RemindersEnabled
	 * @param ReminderInterval ReminderInterval
	 * @param Features Features
	 * @param Trial Trial
	 * @param Expiry Expiry
	 * @param Closed Closed
	 * @description
	 * This class represents an account in the system, encapsulating all relevant properties.
	 * It implements the IAccount interface to ensure type safety and consistency.
	 */
	constructor(
		/** Account ID */
		public ID: number = 0,
		/** Account UID */
		public GUID: string = "",
		/** Account Name */
		public Name: string = "",
		/** Account Description */
		public Description: string = "",
		/** Account owner */
		public Owner: string = "",
		/** Owner ID */
		public OwnerID: number = 0,
		/** Account Marked as disabled*/
		public Disabled: boolean = false,
		/*** Default Account */
		public Default: boolean = false,
		/** Account Address */
		public Address: string = "",
		/** User is an account Admin */
		public Admin: any = 0,
		/** Modified */
		public Modified: number = 0,
		/** RemindersEnabled */
		public RemindersEnabled: boolean = false,
		/** ReminderInterval */
		public ReminderInterval: number = 0,
		/** Features */
		public Features: any = "",
		/** Account is a Trial Account */
		public Trial: boolean = false,
		/** Account Expiry Date */
		public Expiry: number = 0,
		/** Account Closed */
		public Closed: boolean=false
	) {
		if (!Array.isArray(Features)) {
			Features = JSON.parse(Features);
		}
	}
}

/**
 * Interface for Web Application User
 */
export interface IAccountUser {
	/** ID */
	UserID: number;
	/** Public ID from Auth0 (Sub) */
	Sub: any;
	/** User Name */
	Name: string;
	/** Email */
	Email: string;
	/** Admin */
	Admin: boolean;
	/** Site Admin */
	AdminSite: boolean;
	/** Deleted */
	Deleted: boolean;
	/** Disabled */
	Disabled: boolean;
	/** Picture */
	Picture: string;
	/** Group Membership */
	Groups: string
	/** Group Membership IDs*/
	GroupIDs: string
	/** User Wants to Recieve Emails */
	Emails: boolean
	/** AccountUserDisabled */
	AccountUserDisabled: boolean;
	/** Account Role */
	AccountRole: number;
	/** Date Deleted */
	DateDeleted?: number;
	/** Deleted By */
	DeletedBy?: string;
}

/**
 * Interface for Account Group
 */
export interface IAccountGroup {
	/** Account Group ID */
	AccountGroupID: number;
	/** Account ID */
	AccountID: number;
	/** Group Name */
	Name: string;
	/** Admin Group */
	AdminGroup: boolean
	/** System Groop (None Editable) */
	SystemGroup: boolean
	/** Group Members Will Receive Emails */
	ReceiveEmail: boolean
}

/**
 * Interface for Account Group User
 */
export interface IAccountGroupUser {
	/** Account Group User ID */
	AccountGroupUserID: number;
	/** Account Group ID */
	AccountGroupID: number;
	/** Account User ID */
	AccountUserID: number
	/** User Name */
	Name: string;
	/** Selected */
	Selected: boolean
}

/**
 * AccountsService
 */
@Injectable({
	providedIn: 'root'
})
export class AccountsService {

	/** Enable Console Messages */
	private Debug: boolean = false

	/** Default Account */
	private defaultAccountSubject: BehaviorSubject<any>;

	/** Default Account for User (JSON FORMAT) */
	public defaultAccount$: Observable<string>;

	/**
	 * Log message to the Console
	 * @param str
	 */
	consoleLog(str: any) { if (this.Debug == true) console.log(str) }

	/** API Route */
	apiRoute: string = "api/accounts"
	apiRouteGroups: string = "api/AccountGroups"

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private http: HttpClient,
		private globalFunctions: GlobalFunctions
	) {
		try {
			this.defaultAccountSubject = new BehaviorSubject<any>(localStorage.getItem("defaultAccount")!);
			this.defaultAccount$ = this.defaultAccountSubject.asObservable();
		} catch (error) { }
	}

	/**
	 * Update the Default Account
	 * @param defaultAccount
	 */
	setDefaultAccount(defaultAccount: string): void {
		this.defaultAccountSubject.next(defaultAccount)
	}

	/**
	 * Service to return a list of accounts
	* @param queryParams Optional Query Parameters
	 *  userName of logged on user
	 *  showDeleted
	 * @returns IAccount[] Array of accounts
	 * @example
	 * //Copy the Code below
	 * let accounts: IAccount[]
	 * this.accountsService.getAccounts({ "userPublicID": <string>, "showDeleted": <bool> }).subscribe(
	 *      response => { accounts = <IAccount[]>response },
	 *      error => { },
	 *      () => { }
	 * );
	 */
	getAccounts$(queryParams: {} = {}): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	// /**
	//  * Update Accounts
	//  * @param data
	//  * @returns
	//  */
	// updateAccount$(accountID: string, data: IAccount = {} as IAccount): Observable<any> {
	// 	data.Features = JSON.stringify(data.Features)
	// 	let url = `${environment.apiUrl}/${this.apiRoute}`
	// 	return this.globalFunctions.fetchAPI(this.http, url, "PUT", data)
	// }

	/**
	 * Service to Patch accounts
	 * @param userID
	 * @param AccountGUID
	 * @param data
	 * @returns
	 */
	patchAccount$(AccountGUID: string, userID: string, data: IAccount): Observable<any> {
		data.Features = JSON.stringify(data.Features)
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}?userPublicID=${userID}`
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/**
	 * Post a new Account
	 * @param userID
	 * @param data
	 * @returns
	 */
	postAccount$(account: IAccount, userID: string): Observable<any> {
		account.Features = JSON.stringify(account.Features)
		let data = JSON.stringify(account);
		let url = `${environment.apiUrl}/${this.apiRoute}?userPublicID=${userID}`
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data)
	}

	/**
	 * Delete Account
	 * @param userID
	 * @param AccountGUID
	 * @returns
	 */
	deleteAccount$(AccountGUID: string, userID: string) {
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}?userPublicID=${userID}`
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", {})
	}


	/**
	 * Service to return a list of Account users
	 * @param queryParams ID of Account
	 * @returns IWebAppUser[] Array of users
	 * @example
	 * //Copy the Code below
	 * let systems: ISystems[]
	 * this.authenticationService.getAccountUsers({ "systemAccount": <string> , "showDisabled": <bool> }).subscribe(
	 *      response => { users = <IWebAppUser[]>response },
	 *      error => { },
	 *      () => { }
	 * );
	 */
	getAccountUsers$(AccountGUID: string, queryParams: {} = {}): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}/users`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	/**
	 * Service to return a list of Account users
	 * @param AccountGUID
	 * @param userID
	 * @param admin
	 * @param queryParams ID of Account
	 */
	postAccountUsers$(AccountGUID: string, userID: number, admin: boolean = false, queryParams: {} = {}): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}/users?userID=${userID}&admin=${admin}`
		return this.globalFunctions.fetchAPI(this.http, url, "POST", queryParams)
	}

	/**
	 * Remove Account User
	 * @param AccountGUID
	 * @param UserID
	 * @returns
	 */
	deleteAccountUser$(AccountGUID: string, UserID: number): Observable<any> {
		let data = {}
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}/users/${UserID}`
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data)
	}

	/**
	 * Patch Account user
	 * @param AccountGUID
	 * @param UserID
	 * @param user
	 * @returns
	 */
	patchAccountUser$(AccountGUID: string, UserID: number, user: IAccountUser): Observable<any> {
		let data = JSON.stringify(user);
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}/users/${UserID}`
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}


	/**
	 * Service to return a list of Account Groups
	 * @param AccountGUID
	 * @param queryParams ID of Account
	 * @returns IAccountGroup[] Array of Account Groups
	 */
	getAccountGroups$(AccountGUID: string, queryParams: {} = {}): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRouteGroups}/${AccountGUID}`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	/**
	 * Service to add a new Account Group
	 * @param AccountGUID
	 * @param group Group IAccountGroup
	 * @returns IAccountGroup[] Array of Account Groups
	 */
	postAccountGroup$(AccountGUID: string, group: IAccountGroup): Observable<any> {
		let data = JSON.stringify(group);
		let url = `${environment.apiUrl}/${this.apiRouteGroups}/${AccountGUID}`
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data)
	}

	/**
	 * Service to patch an Account Group
	 * @param AccountGUID
	 * @param groupID
	 * @returns IAccountGroup
	 */
	patchAccountGroup$(AccountGUID: string, groupID: number, group: IAccountGroup): Observable<any> {
		let data = JSON.stringify(group);
		let url = `${environment.apiUrl}/${this.apiRouteGroups}/${AccountGUID}/${groupID}`
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/**
	 * Service to Delete an Account Group
	 * @param AccountGUID
	 * @param groupID
	 * @returns IAccountGroup
	 */
	deleteAccountGroup$(AccountGUID: string, groupID: number): Observable<any> {
		let data = {};
		let url = `${environment.apiUrl}/${this.apiRouteGroups}/${AccountGUID}/${groupID}`
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data)
	}

	/**
	 * Service to return a list of Account Group Users
	 * @param AccountGUID
	 * @param groupID
	 * @param queryParams ID of Account
	 * @returns IAccountGroupUser[] Array of Account Group Users
	 */
	getAccountGroupUsers$(AccountGUID: string, groupID: number, queryParams: {} = {}): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRouteGroups}/${AccountGUID}/${groupID}/users`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	/**
	 * Service to patch a list of Account Group Users
	 * @param AccountGUID
	 * @param group
	 * @returns IAccountGroupUser[] Array of Account Group Users
	 */
	patchAccountGroupUsers$(AccountGUID: string, groupID: number, groupUsers: IAccountGroupUser[]): Observable<any> {
		let data = JSON.stringify(groupUsers);
		let url = `${environment.apiUrl}/${this.apiRouteGroups}/${AccountGUID}/${groupID}/users`
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/**
	 * getAllFeatures
	 * @returns 
	 */
	getAllFeatures$(): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}/features`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", {})
	}

	/**
	 * patchAccountFeatures
	 * @param AccountGUID AccountGUID 
	 * @param input input 
	 * @returns 
	 */
	patchAccountFeatures$(AccountGUID: string, input: any): Observable<any> {
		let data = JSON.stringify(input);
		let url = `${environment.apiUrl}/${this.apiRoute}/${AccountGUID}/features`
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/**
	 * getAccountGUID
	 * @param organisationUID organisationUID  
	 * @returns 
	 */
	getAccountGUID$(organisationUID: string): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}/${organisationUID}`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", {})
	}

	/**
	 * checkFeature
	 * Check that an account HAS a feature assigned AND is Enabled
	 * @param feature feature   
	 * @param selectedAccount Selected account to check
	 * @returns 
	 */
	static checkFeature(feature: AccountFeatures, account: Account): boolean {
		try {
			if (account && account.Features) {
				return account.Features.some((x: any) => x.FeatureID == feature && x.Enabled);
			}
			return false;
		} catch (error) {
			return false;
		}
		
	}

	/**
	 * hasFeature
	 * Check that an account HAS a feature assigned
	 * @param feature feature   
	 * @param selectedAccount Selected account to check
	 * @returns 
	 */
	static hasFeature(feature: AccountFeatures, account: Account): boolean {
		if (account && account.Features) {
			return account.Features.some((x: any) => x.FeatureID == feature);
		}
		return false;
	}
}
